import type { FetchResult } from '@apollo/client';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { navigate } from 'gatsby';
import React, { useState } from 'react';
import { pdfjs } from 'react-pdf';
import type { DocumentCallback } from 'react-pdf/dist/cjs/shared/types.js';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import { DISCERN_JOBS } from '@constants/routes';

import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import type { UpdateDiscernDocumentMutation } from 'generated/discern.generated';
import {
  ListDiscernDocumentsDocument,
  useCreateDiscernDocumentFromUploadMutation,
  useGetDiscernDocumentQuery,
  useStartAutoCurationMutation,
  useUpdateDiscernDocumentMutation,
} from 'generated/discern.generated';

import PdfSplitEditor from './PdfSplitEditor';
import { DocumentPreview } from './components/DocumentPreview';
import { GridViewControls } from './components/GridViewControls';
import { SaveGroupsPromptDialog } from './components/SaveGroupsPromptDialog';
import { ViewToggle } from './components/ViewToggle';
import { selectionStore } from './utils/SelectionStore';
import { mergeSelectedPages, splitSelectedGroups } from './utils/merge';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfSplitContainerProps {
  binaryId?: string; // Required for PDF preview and startAutoCuration
  vitallPatientId: string;
  discernDocumentId?: string; // Optional - only for existing documents
}

/**
 * Container component that coordinates state between PdfPreview and PdfSplitEditor
 *
 * This component:
 * - Manages the total page count from PDF loading
 * - Tracks the current page as the user scrolls
 * - Passes this state down to both child components
 * - Provides callbacks for saving split configurations
 */

const PdfSplitContainer: React.FC<PdfSplitContainerProps> = ({
  binaryId,
  vitallPatientId,
  discernDocumentId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [updateDiscernDocument] = useUpdateDiscernDocumentMutation();
  const [createDiscernDocument] = useCreateDiscernDocumentFromUploadMutation();
  const [startAutoCuration] = useStartAutoCurationMutation();
  // Use active profile to get patient ID (this view should always be used in curation mode)
  const {
    state: { activeProfile },
  } = useSession();
  const patientId = activeProfile?.patient_id;

  // Track document ID (may be created on first save)
  const [currentDocumentId, setCurrentDocumentId] = useState<
    string | undefined
  >(discernDocumentId);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [selectedPage, setSelectedPage] = React.useState<number | undefined>(
    undefined
  );

  const [viewMode, setViewMode] = useState<'standard' | 'grid'>('standard');
  const [pageGroups, setPageGroups] = useState<number[][]>([]);
  const [selectedPages, setSelectedPages] = useState<number[]>([]);
  const [deletionHistory, setDeletionHistory] = useState<number[][]>([]);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [groupsSaved, setGroupsSaved] = useState<boolean>(false);
  const [showSaveDialog, setShowSaveDialog] = useState<boolean>(false);
  const justSavedRef = React.useRef<boolean>(false);

  // Fetch document to load saved grouping state (only if documentId exists)
  const documentIdToFetch = currentDocumentId || discernDocumentId;
  const { data: documentData, loading: documentLoading } =
    useGetDiscernDocumentQuery({
      variables: {
        input: {
          discern_document_id: documentIdToFetch!,
        },
      },
      skip: !documentIdToFetch,
    });

  const removedPages = deletionHistory.flat();
  const pageGroupsKey = JSON.stringify(pageGroups);

  // Load saved grouping state from document
  React.useEffect(() => {
    // If already initialized, skip
    if (isInitialized) {
      return;
    }

    // If still loading document, wait
    if (documentLoading) {
      return;
    }

    // If no document exists (no documentIdToFetch), mark as initialized immediately
    // This allows the initialization useEffect to run and create default groups
    if (!documentIdToFetch) {
      setIsInitialized(true);
      return;
    }

    // If we have a documentId but no document data yet, wait
    if (!documentData?.getDiscernDocument) {
      return;
    }

    // We have document data, check for saved groups
    const groupingState = documentData.getDiscernDocument.grouping_state_json;
    const savedGroups = (groupingState as { pageGroups?: number[][] })
      .pageGroups;

    if (savedGroups && Array.isArray(savedGroups) && savedGroups.length > 0) {
      // Filter out any pages that have been removed
      const filteredGroups = savedGroups
        .map((group) => group.filter((page) => !removedPages.includes(page)))
        .filter((group) => group.length > 0); // Remove empty groups

      if (filteredGroups.length > 0) {
        setPageGroups(filteredGroups);
        setGroupsSaved(true); // Groups are loaded from saved state
        setIsInitialized(true);
        return;
      }
    }

    // If no saved groups found, mark as initialized so we don't keep checking
    setIsInitialized(true);
    // Don't reset groupsSaved here - it will be set to false when groups change
    // via handleGroupsChange or onReorderPages
  }, [
    documentData,
    isInitialized,
    documentLoading,
    removedPages,
    documentIdToFetch,
  ]);

  // Initialize to all pages split when totalPages is set and no saved groups exist
  React.useEffect(() => {
    // Don't initialize if still loading document (wait for it to finish)
    if (documentLoading) {
      return;
    }

    // If we have a document, wait for it to be checked for saved groups
    if (documentIdToFetch && !isInitialized) {
      return;
    }

    // Only initialize if:
    // 1. We have totalPages
    // 2. pageGroups is empty
    // 3. No saved groups exist (either no document or document has no saved groups)
    const pageGroupsFromDoc =
      documentData?.getDiscernDocument.grouping_state_json.pageGroups;
    const hasSavedGroups =
      pageGroupsFromDoc !== null &&
      Array.isArray(pageGroupsFromDoc) &&
      pageGroupsFromDoc.length > 0;

    if (
      totalPages > 0 &&
      pageGroups.length === 0 &&
      (!documentIdToFetch || (isInitialized && !hasSavedGroups))
    ) {
      // No document or no saved groups, initialize to individual pages
      const allPages = Array.from({ length: totalPages }, (_, i) => [i + 1]);
      setPageGroups(allPages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    totalPages,
    pageGroupsKey,
    isInitialized,
    documentIdToFetch,
    documentData,
    documentLoading,
  ]);

  // Sync groupsSaved state when document data updates (e.g., after save)
  React.useEffect(() => {
    if (!documentData?.getDiscernDocument || !isInitialized) {
      return;
    }

    // Don't override groupsSaved if we just saved (within 5 seconds)
    // This prevents the sync from interfering immediately after save
    if (justSavedRef.current) {
      return;
    }

    const groupingState = documentData.getDiscernDocument.grouping_state_json;
    const savedGroups =
      'pageGroups' in groupingState
        ? (groupingState as { pageGroups?: number[][] }).pageGroups
        : undefined;

    if (savedGroups && Array.isArray(savedGroups) && savedGroups.length > 0) {
      // Compare current pageGroups with saved groups
      // Normalize both arrays for comparison (sort nested arrays)
      const normalizeGroups = (groups: number[][]) =>
        groups
          .map((g) => [...g].sort((a, b) => a - b))
          .sort((a, b) => a[0] - b[0]);
      const currentGroupsNormalized = normalizeGroups(pageGroups);
      const savedGroupsNormalized = normalizeGroups(savedGroups);
      const currentGroupsStr = JSON.stringify(currentGroupsNormalized);
      const savedGroupsStr = JSON.stringify(savedGroupsNormalized);
      const groupsMatch = currentGroupsStr === savedGroupsStr;

      // Update state based on comparison
      setGroupsSaved(groupsMatch);
    } else if (
      !savedGroups ||
      (Array.isArray(savedGroups) && savedGroups.length === 0)
    ) {
      // No saved groups found - mark as unsaved if we have current groups
      if (pageGroups.length > 0) {
        setGroupsSaved(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentData, pageGroupsKey, isInitialized]);

  const handleLoadSuccess = (document: DocumentCallback) => {
    setTotalPages(document.numPages);
  };

  const handlePageClick = (page: number) => {
    setCurrentPage(page);
    setSelectedPage(page);
  };

  const handleToggleViewMode = () => {
    setViewMode(viewMode === 'standard' ? 'grid' : 'standard');
  };
  // Save groups only (called by Save button)
  const handleSaveGroupsOnly = async (
    _splits: number[],
    pageGroups: number[][]
  ) => {
    try {
      // Validate required fields
      if (!binaryId) {
        enqueueSnackbar({
          variant: 'error',
          message: 'Binary ID is required.',
        });
        return;
      }

      // Validate page groups
      if (pageGroups.length === 0) {
        enqueueSnackbar({
          variant: 'error',
          message:
            'Page groups are required. Please create at least one group.',
        });
        return;
      }

      // Set saving state - disable save button and prevent brain button clicks
      setIsSaving(true);

      let documentId = currentDocumentId || discernDocumentId;
      let result: FetchResult<UpdateDiscernDocumentMutation>;

      if (!documentId) {
        // Create new document if it doesn't exist
        // Get filename from URL params or use default
        const urlParams = new URLSearchParams(window.location.search);
        const fileName = urlParams.get('fileName') || 'document.pdf';

        const createResult = await createDiscernDocument({
          variables: {
            input: {
              vitall_patient_id: vitallPatientId,
              binary_resource_id: binaryId,
              filename: fileName,
            },
          },
          refetchQueries: [
            {
              query: ListDiscernDocumentsDocument,
              variables: {
                input: {
                  vitall_patient_id: vitallPatientId,
                },
              },
            },
          ],
        });

        if (!createResult.data?.createDiscernDocumentFromUpload) {
          throw new Error('Failed to create document');
        }

        documentId =
          createResult.data.createDiscernDocumentFromUpload.discern_document_id;
        setCurrentDocumentId(documentId); // Store for future saves

        // Update URL with documentId for future reference
        if (typeof window !== 'undefined') {
          const urlParams = new URLSearchParams(window.location.search);
          urlParams.set('discernDocumentId', documentId);
          const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
          window.history.replaceState({}, '', newUrl);
        }

        // Now update it with page groups
        const updatedState = {
          filename: fileName,
          pageGroups,
        };

        result = await updateDiscernDocument({
          variables: {
            input: {
              discern_document_id: documentId,
              status: 'Preparation',
              grouping_state_json: updatedState,
            },
          },
        });
      } else {
        // Update existing document
        // Preserve existing grouping_state_json data (filename, _metadata, etc.)
        const currentState =
          documentData?.getDiscernDocument.grouping_state_json || {};
        const updatedState = {
          ...currentState,
          pageGroups,
        };

        result = await updateDiscernDocument({
          variables: {
            input: {
              discern_document_id: documentId,
              status: 'Preparation',
              grouping_state_json: updatedState,
            },
          },
        });
      }

      // Verify the mutation succeeded (check response)
      if (result.data?.updateDiscernDocument) {
        // Mark groups as saved immediately - this enables the brain button right away
        // The sync useEffect will maintain this state when it runs
        justSavedRef.current = true;
        setGroupsSaved(true);

        enqueueSnackbar({
          variant: 'success',
          message: 'Page groups saved successfully!',
        });

        // Reset the just-saved flag after a longer delay to ensure sync useEffect doesn't interfere
        // This gives time for the refetch to complete and the sync to properly validate
        setTimeout(() => {
          justSavedRef.current = false;
        }, 5000);
      } else {
        throw new Error('Save mutation returned no data');
      }
    } catch (error: any) {
      // Rollback: ensure saved state is false on error
      setGroupsSaved(false);
      justSavedRef.current = false;

      enqueueSnackbar({
        variant: 'error',
        message:
          error.message || 'Failed to save page groups. Please try again.',
      });
    } finally {
      // Always disable saving state
      setIsSaving(false);
    }
  };

  // Start auto curation (called by brain button)
  const handleStartAutoCuration = async (
    _splits: number[],
    pageGroups: number[][]
  ) => {
    // For new uploads, we don't need to check if groups are saved
    // For existing documents, check if groups have been saved
    if (discernDocumentId) {
      const isSaved = groupsSaved;
      if (!isSaved) {
        setShowSaveDialog(true);
        return;
      }
    }

    try {
      // Validate binaryId is provided (required for PDF preview)
      if (!binaryId) {
        enqueueSnackbar({
          variant: 'error',
          message: 'Binary ID is required to load PDF preview.',
        });
        return;
      }

      // Validate page groups
      if (pageGroups.length === 0) {
        enqueueSnackbar({
          variant: 'error',
          message:
            'Page groups are required. Please create at least one group.',
        });
        return;
      }

      // Set loading state
      setIsProcessing(true);

      // Convert pageGroups to integers (pages are whole numbers)
      const intPageGroups = pageGroups.map((group) =>
        group.map((page) => Math.floor(page))
      );

      let documentId: string;

      if (binaryId) {
        // Use startAutoCuration with binary_id
        // This will create/update document, call Discern API, and create job
        // Note: Upload is already complete, so we don't need multipartUploadInfo
        const result = await startAutoCuration({
          variables: {
            input: {
              binary_id: binaryId,
              vitall_patient_id: vitallPatientId,
              pageGroups: intPageGroups,
            },
          },
        });

        if (!result.data?.startAutoCuration) {
          throw new Error('Failed to start auto curation');
        }

        documentId = result.data.startAutoCuration.discern_document_id;
      } else {
        throw new Error('binaryId is required to start auto curation');
      }

      // Show success notification
      enqueueSnackbar({
        variant: 'success',
        message: `Auto curation started successfully! Redirecting to job view...`,
      });

      // Navigate to job drilldown page to show processing state while results are being processed
      navigate(`${DISCERN_JOBS}${documentId}`);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to start auto curation. Please try again.';

      enqueueSnackbar({
        variant: 'error',
        message: errorMessage,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGroupsChange = React.useCallback((groups: number[][]) => {
    setPageGroups(groups);
    setGroupsSaved(false); // Mark as unsaved when groups change
  }, []);

  const handleGroupSelectedPages = () => {
    const newGroups = mergeSelectedPages(pageGroups, selectedPages, totalPages);
    setPageGroups(newGroups);
    handleClearSelection();
  };

  const handleSelectionChange = (pages: number[]) => {
    setSelectedPages(pages);
    selectionStore.setSelectedPages(pages);
  };

  const handleClearSelection = () => {
    setSelectedPages([]);
    selectionStore.clear();
  };

  const handleRemovePage = (pageNum: number) => {
    setDeletionHistory((prev) => [...prev, [pageNum]]);
  };

  const handleRestorePage = (pagesToRestore?: number[]) => {
    setDeletionHistory((prev: number[][]) => {
      if (prev.length === 0) return prev;

      if (pagesToRestore && pagesToRestore.length > 0) {
        return prev.map((batch) =>
          batch.filter((page) => !pagesToRestore.includes(page))
        );
      }

      return prev.slice(0, -1);
    });
  };

  const handleDeleteSelectedPages = () => {
    if (selectedPages.length === 0) return;

    const pagesToDelete = selectedPages.filter((page) => {
      const group = pageGroups.find((g) => g.includes(page));
      return !group || group.length === 1;
    });

    if (pagesToDelete.length === 0) return;

    setDeletionHistory((prev) => [...prev, [...pagesToDelete]]);
    handleClearSelection();
  };

  const handleSplitSelectedGroups = () => {
    const newGroups = splitSelectedGroups(
      pageGroups,
      selectedPages,
      totalPages
    );
    setPageGroups(newGroups);
    handleClearSelection();
  };

  // No need to show loading here - PDF components handle their own loading states
  if (!binaryId) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
        }}
      >
        <Typography>No binary ID provided</Typography>
      </Box>
    );
  }
  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          height: '700px',
        }}
        spacing={1}
      >
        <Stack sx={{ height: '100%', width: '69%' }}>
          {/* Left Side - PDF Preview */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6" color="textPrimary">
              PDF Preview
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Group and Clear buttons - only show in grid view when pages are selected */}
              {viewMode === 'grid' && (
                <GridViewControls
                  onClearSelection={handleClearSelection}
                  onDeleteSelectedPages={handleDeleteSelectedPages}
                  onGroupSelectedPages={handleGroupSelectedPages}
                  onSplitSelectedGroups={handleSplitSelectedGroups}
                  pageGroups={pageGroups}
                  selectedPages={selectedPages}
                />
              )}
              <ViewToggle
                viewMode={viewMode}
                onToggleViewMode={handleToggleViewMode}
              />
            </Box>
          </Box>
          <Paper elevation={2} sx={{ flex: 1, overflow: 'hidden' }}>
            <DocumentPreview
              binaryId={binaryId}
              patientId={patientId}
              viewMode={viewMode}
              pageGroups={pageGroups}
              totalPages={totalPages}
              selectedPage={selectedPage}
              removedPages={removedPages}
              onLoadSuccess={handleLoadSuccess}
              onPageClick={handlePageClick}
              onSelectionChange={handleSelectionChange}
              onRemovePage={handleRemovePage}
              onChangeCurrentPage={setCurrentPage}
              onReorderPages={setPageGroups}
            />
          </Paper>
        </Stack>

        {/* Right Side - Split Editor */}
        {totalPages > 0 && (
          <PdfSplitEditor
            totalPages={totalPages}
            currentPage={currentPage}
            onSaveSplits={handleSaveGroupsOnly}
            onStartAutoCuration={handleStartAutoCuration}
            onPageClick={handlePageClick}
            isSaving={isSaving}
            removedPages={removedPages}
            onRemovePage={handleRemovePage}
            onRestorePage={handleRestorePage}
            onGroupsChange={handleGroupsChange}
            onReorderPages={(newGroups) => {
              setPageGroups(newGroups);
              setGroupsSaved(false); // Mark as unsaved when groups are reordered
            }}
            externalPageGroups={
              isInitialized && totalPages > 0 ? pageGroups : undefined
            }
            isProcessing={isProcessing}
            discernDocumentId={discernDocumentId}
          />
        )}
      </Stack>

      <SaveGroupsPromptDialog
        open={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
      />
    </Box>
  );
};

export default PdfSplitContainer;
